import axios from 'axios';
import { getPartnerToken, getPartnerRestaurantId, setPartnerSession, setPartnerSessionSwitching } from './partnerSessionStorage';
import { waitForCloudRequestsToSettle } from '@/services/http/deviceHeaderInterceptor';
import { getPendingActionStats } from '@/services/offline/pendingActionQueue';
import { clearPartnerProfileMemory } from './partnerProfileService';
import { clearPartnerSessionCache } from '@/services/offline/partnerSessionCache';
import { clearCachedOperationalPolicy } from '@/services/device/deviceOperationalPolicy';
import { clearPersistedOperationalAuthority } from '@/store/modules/operationalMode';
import { stopDeviceHeartbeat } from '@/services/device/deviceHeartbeatService';
import { stopLocalControlRuntime } from '@/services/localHub/localControlRuntime';
import { clearOfflineSession } from '@/services/offline/offlineUserAccess';
import { isSessionRevisionPaused } from './sessionRevisionPause';
import { OFFLINE_MODE_ENABLED } from '@/services/offline/offlineFeature';

// Session selection does not depend on a restaurant device or an offline credential.
const client = axios.create({ baseURL: process.env.VUE_APP_API_URL, timeout: 10000 });
const headers = token => ({ Authorization: `Bearer ${token || getPartnerToken()}` });
export async function listPartnerRestaurants(token) {
  return (await client.get('/v1/api/partner/restaurants', { headers: headers(token) })).data;
}
export async function selectPartnerRestaurant(restaurantId, token) {
  return (await client.post('/v1/api/partner/select-restaurant', { restaurant_id: restaurantId }, { headers: headers(token) })).data;
}
export function hasUnfinishedFinancialAction(restaurantId) {
  if (!restaurantId || typeof localStorage === 'undefined') return false;
  const prefixes = ['ewu.cashlogy-checkout.v1:', 'ewu.account-credit.v1:', 'ewu.cashlogy-refund.v1:', 'ewu:cashlogy-control:', 'ewu:cashlogy-change:'];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key?.includes(restaurantId) || !prefixes.some(prefix => key.startsWith(prefix))) continue;
    const action = JSON.parse(localStorage.getItem(key) || 'null');
    if (!action) continue;
    if (key.startsWith('ewu:cashlogy-control:')) {
      if (!['succeeded', 'failed', 'cancelled', 'rejected'].includes(action.result?.state)) return true;
    } else if (key.startsWith('ewu:cashlogy-change:') || action.state !== 'completed') return true;
  }
  return false;
}
export async function switchPartnerRestaurant(restaurantId, store) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) throw new Error('Conéctate a internet para cambiar de restaurante.');
  if (store?.state?.operationalMode?.mode !== 'CLOUD_MODE') throw new Error('Finaliza el modo local antes de cambiar de restaurante.');
  if (restaurantId === getPartnerRestaurantId()) return;
  if (isSessionRevisionPaused() || hasUnfinishedFinancialAction(getPartnerRestaurantId())) {
    throw new Error('Finaliza o revisa la operación de caja o fidelización pendiente antes de cambiar de restaurante.');
  }
  setPartnerSessionSwitching(true);
  let changed = false;
  try {
    if (!await waitForCloudRequestsToSettle()) throw new Error('Hay una operación en curso. Espera a que termine y vuelve a intentarlo.');
    const current = getPartnerRestaurantId();
    if (current && OFFLINE_MODE_ENABLED) {
      const stats = await getPendingActionStats(current);
      if (stats.pending || stats.processing || stats.failed || stats.conflicts) throw new Error('Sincroniza las operaciones pendientes antes de cambiar de restaurante.');
    }
    const session = await selectPartnerRestaurant(restaurantId);
    stopDeviceHeartbeat();
    stopLocalControlRuntime();
    clearPartnerProfileMemory();
    clearPartnerSessionCache();
    clearCachedOperationalPolicy();
    clearPersistedOperationalAuthority();
    await clearOfflineSession();
    setPartnerSession(session);
    changed = true;
    // A fresh document disposes forms, sockets and background workers from the old context.
    window.location.assign('/TPVHub');
  } finally {
    if (!changed) setPartnerSessionSwitching(false);
  }
}
export function restaurantAccessError(error) {
  const detail = error?.response?.data?.detail;
  return detail?.message || (typeof detail === 'string' ? detail : '') || error?.response?.data?.message || error?.response?.data?.error || error?.message || 'No se pudo acceder al restaurante.';
}
